import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import { UNLOCODE_API, UNLOCODE_KEY } from 'src/app/shared/app-token';
import { QueryUnlocodes } from 'src/app/types/query-unlocode';
import { PagedResponse } from '../types/paged-response';
import { Unlocode } from '../types/unlocode';
import { SharedService } from './shared.service';

/**
 * Un/Locode service
 * @class UnlocodeService
 * @typedef {UnlocodeService}
 */
@Injectable( {
    providedIn: 'root'
} )
export class UnlocodeService {
    /** ignore in coverage */
    #apiUrl = inject( UNLOCODE_API );
    #apiKey = inject( UNLOCODE_KEY );
    #httpClient = inject( HttpClient );
    #sharedService = inject( SharedService );

    /**
     * get unlocode by id
     * @param id            id of unlocode
     * @returns {object}    json object with unlocode details
     */
    getUnlocodeById( id: string ): Observable<Unlocode> {
        let headers = new HttpHeaders();
        headers = headers.set( 'Authorization', `${this.#apiKey}` );

        return this.#httpClient.get<Unlocode>(
            `${this.#apiUrl}/unlocode/${id}`,
            { headers: headers }
        ).pipe(
            map( ( unlocode ) => unlocode ),
            catchError( ( err: any ) => {
                if ( err instanceof HttpErrorResponse ) {
                    try {
                        this.#sharedService.handleServerSideError( err );
                    } catch ( e ) {
                        console.log( 'A server error occurred', e );
                    }
                }
                return of( err );
            } )
        );
    }

    /**
     * get unlocode name
     * @param id            id of unlocode
     * @returns {string}    unlocode name as string
     */
    getUnlocodeName( id: string ): Observable<string> {
        let headers = new HttpHeaders();
        headers = headers.set( 'Authorization', `${this.#apiKey}` );

        return this.#httpClient.get<Unlocode>(
            `${this.#apiUrl}/unlocode/${id}`,
            { headers: headers }
        ).pipe(
            map( ( unlocode ) => unlocode.name ),
            catchError( ( err: any ) => {
                console.log( 'A server error occurred' );
                return of( err );
            } )
        );
    }

    /**
     * method to handle paginated and queried page requests on static data
     * @param {QueryUnlocodes} query query object
     * @returns {object} json object with paged results
     */
    getFilteredUnlocodes( query: QueryUnlocodes ) {
        let headers = new HttpHeaders();
        headers = headers.set( 'Authorization', `${this.#apiKey}` );

        const parameters = new URLSearchParams( { ...query } );

        return this.#httpClient
            .get<PagedResponse>(
                `${this.#apiUrl}/unlocode?${parameters}`,
                { headers: headers }
            ).pipe( map( ( result ) => result ) );
    }
}
